import {
  X, RotateCcw, Type, AlignJustify, AlignLeft, BookOpen,
  Contrast, Circle, ImageOff, Sparkles
} from 'lucide-react';
import { useAccessibility, AccessibilitySettings } from './AccessibilityContext';
import { cn } from '@/lib/utils';

interface SettingButtonProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function SettingButton({ icon, label, isActive, onClick }: SettingButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all",
        "hover:border-primary hover:bg-primary/5",
        isActive
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-foreground"
      )}
    >
      <div className="text-2xl">{icon}</div>
      <span className="text-base font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

interface AccessibilityWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccessibilityWidget({ isOpen, onClose }: AccessibilityWidgetProps) {
  const { settings, toggleSetting, resetSettings, getActiveSettingsList } = useAccessibility();
  const activeSettings = getActiveSettingsList();

  const textSettings: { key: keyof AccessibilitySettings; icon: React.ReactNode; label: string }[] = [
    { key: 'largerText', icon: <Type className="h-6 w-6" />, label: 'Größerer Text' },
    { key: 'lineHeight', icon: <AlignJustify className="h-6 w-6" />, label: 'Zeilenhöhe' },
    { key: 'textAlignment', icon: <AlignLeft className="h-6 w-6" />, label: 'Textausrichtung' },
    { key: 'readableFont', icon: <BookOpen className="h-6 w-6" />, label: 'Lesbare Schrift' },
  ];

  const visualSettings: { key: keyof AccessibilitySettings; icon: React.ReactNode; label: string }[] = [
    { key: 'contrast', icon: <Contrast className="h-6 w-6" />, label: 'Kontrast' },
    { key: 'grayscale', icon: <Circle className="h-6 w-6" />, label: 'Graustufen' },
    { key: 'hideImages', icon: <ImageOff className="h-6 w-6" />, label: 'Bilder ausblenden' },
    { key: 'reduceAnimations', icon: <Sparkles className="h-6 w-6" />, label: 'Animationen reduzieren' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div
        className={cn(
          "fixed left-6 bottom-6 z-50",
          "w-[340px] max-h-[calc(100vh-48px)]",
          "bg-background border border-border rounded-xl shadow-2xl",
          "overflow-hidden flex flex-col"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
          <h2 className="font-semibold text-sm text-foreground">Barrierefreiheit</h2>
          <div className="flex items-center gap-1">
            <button onClick={resetSettings} className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Einstellungen zurücksetzen" title="Einstellungen zurücksetzen">
              <RotateCcw className="h-4 w-4 text-muted-foreground" />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Schließen">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-5">
          {/* Text Section */}
          <div>
            <h3 className="text-base font-semibold text-muted-foreground uppercase tracking-wider mb-3">Text</h3>
            <div className="grid grid-cols-2 gap-3">
              {textSettings.map((s) => (
                <SettingButton key={s.key} icon={s.icon} label={s.label}
                  isActive={settings[s.key]} onClick={() => toggleSetting(s.key)} />
              ))}
            </div>
          </div>

          {/* Visual Section */}
          <div>
            <h3 className="text-base font-semibold text-muted-foreground uppercase tracking-wider mb-3">Visuell</h3>
            <div className="grid grid-cols-2 gap-3">
              {visualSettings.map((s) => (
                <SettingButton key={s.key} icon={s.icon} label={s.label}
                  isActive={settings[s.key]} onClick={() => toggleSetting(s.key)} />
              ))}
            </div>
          </div>

          {/* Active Settings */}
          <div>
            <h3 className="text-base font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Aktive Einstellungen
            </h3>
            <div className="bg-muted/50 rounded-lg p-3 min-h-[48px]">
              {activeSettings.length === 0 ? (
                <p className="text-base text-muted-foreground">Keine aktiven Einstellungen</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activeSettings.map((s) => (
                    <span key={s.key}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-base font-medium rounded-md">
                      {s.label}
                      <button onClick={() => toggleSetting(s.key)}
                        className="hover:bg-primary/20 rounded p-0.5"
                        aria-label={`${s.label} deaktivieren`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
